import { PageController } from '@client/components/_core/layout/PageController';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { useThemeStore } from '@client/stores/themeStore';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Divider, Form, Space, Switch, Typography } from 'antd';
import { Moon, Sun } from 'lucide-react';
import { useState } from 'react';

const { Title, Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/component-showcase/date-picker-test')({
    staticData: {
        breadcrumb: { title: 'DatePicker Test' },
        featureFlag: 'enable-debug-info',
    },
    component: DatePickerTestPage,
});

function DatePickerTestPage() {
    const { theme, setTheme } = useThemeStore();
    const isDark =
        theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    // State for different date picker demos
    const [dateValue, setDateValue] = useState<Date | null>(null);
    const [dateTimeValue, setDateTimeValue] = useState<Date | null>(null);
    const [isoDateValue, setIsoDateValue] = useState<string | null>(null);
    const [isoDateTimeValue, setIsoDateTimeValue] = useState<string | null>(null);
    const [dateOnlyValue, setDateOnlyValue] = useState<Date | null>(null);
    const [constrainedDate, setConstrainedDate] = useState<Date | null>(null);

    // Min/max dates for constrained picker
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - 7); // 7 days ago
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30); // 30 days from now

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-4xl p-6">
                {/* Header */}
                <div className="mb-8 flex items-start justify-between">
                    <div>
                        <Title level={2}>DatePicker Test Page</Title>
                        <Paragraph className="max-w-2xl text-muted-foreground">
                            Test the updated DatePicker component with native HTML5 inputs on mobile and Ant Design on
                            desktop. Resize your browser or test on a mobile device to see the native picker.
                        </Paragraph>
                    </div>
                    <div className="flex items-center gap-3 rounded-lg border border-separator bg-card px-4 py-2">
                        <Sun size={16} className={!isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                        <Switch
                            checked={isDark}
                            onChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                            checkedChildren={<Moon size={12} />}
                            unCheckedChildren={<Sun size={12} />}
                        />
                        <Moon size={16} className={isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                    </div>
                </div>

                {/* Basic Date Picker */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Basic Date Picker
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Returns Date objects (default behaviour)
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <div className="space-y-6">
                        <div>
                            <Text strong className="mb-2 block">
                                Date Only
                            </Text>
                            <Space direction="vertical" className="w-full">
                                <DatePicker
                                    value={dateValue}
                                    onChange={(date) => setDateValue(date as Date | null)}
                                    placeholder="Select a date"
                                />
                                <Text type="secondary" className="text-xs">
                                    Value: {dateValue ? dateValue.toISOString() : 'null'}
                                </Text>
                            </Space>
                        </div>

                        <Divider />

                        <div>
                            <Text strong className="mb-2 block">
                                Date and Time
                            </Text>
                            <Space direction="vertical" className="w-full">
                                <DatePicker
                                    value={dateTimeValue}
                                    onChange={(date) => setDateTimeValue(date as Date | null)}
                                    showTime
                                    placeholder="Select date and time"
                                />
                                <Text type="secondary" className="text-xs">
                                    Value: {dateTimeValue ? dateTimeValue.toISOString() : 'null'}
                                </Text>
                            </Space>
                        </div>
                    </div>
                </Card>

                {/* ISO String Output */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                ISO String Output
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Uses outputFormat=&quot;iso&quot; for backwards compatibility
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <div className="space-y-6">
                        <div>
                            <Text strong className="mb-2 block">
                                Date Only (ISO)
                            </Text>
                            <Space direction="vertical" className="w-full">
                                <DatePicker
                                    value={isoDateValue}
                                    onChange={(date) => setIsoDateValue(date as string | null)}
                                    outputFormat="iso"
                                    placeholder="Select a date"
                                />
                                <Text type="secondary" className="text-xs">
                                    Value: {isoDateValue ? `"${isoDateValue}"` : 'null'}
                                </Text>
                            </Space>
                        </div>

                        <Divider />

                        <div>
                            <Text strong className="mb-2 block">
                                Date and Time (ISO)
                            </Text>
                            <Space direction="vertical" className="w-full">
                                <DatePicker
                                    value={isoDateTimeValue}
                                    onChange={(date) => setIsoDateTimeValue(date as string | null)}
                                    showTime
                                    outputFormat="iso"
                                    placeholder="Select date and time"
                                />
                                <Text type="secondary" className="text-xs">
                                    Value: {isoDateTimeValue ? `"${isoDateTimeValue}"` : 'null'}
                                </Text>
                            </Space>
                        </div>
                    </div>
                </Card>

                {/* dateOnly Mode */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Date Only Mode (UTC Noon)
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Uses dateOnly prop - stores dates at UTC noon to avoid timezone issues
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <Space direction="vertical" className="w-full">
                        <DatePicker
                            value={dateOnlyValue}
                            onChange={(date) => setDateOnlyValue(date as Date | null)}
                            dateOnly
                            placeholder="Select a date (stored at UTC noon)"
                        />
                        <Text type="secondary" className="text-xs">
                            Value: {dateOnlyValue ? dateOnlyValue.toISOString() : 'null'}
                        </Text>
                        <Text type="secondary" className="text-xs italic">
                            Note: Time should always be 12:00:00.000Z
                        </Text>
                    </Space>
                </Card>

                {/* Min/Max Constraints */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Date Constraints
                            </Title>
                            <Text type="secondary" className="text-sm">
                                With minDate (7 days ago) and maxDate (30 days from now)
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <Space direction="vertical" className="w-full">
                        <DatePicker
                            value={constrainedDate}
                            onChange={(date) => setConstrainedDate(date as Date | null)}
                            minDate={minDate}
                            maxDate={maxDate}
                            placeholder="Select within allowed range"
                        />
                        <Text type="secondary" className="text-xs">
                            Value: {constrainedDate ? constrainedDate.toISOString() : 'null'}
                        </Text>
                        <Text type="secondary" className="text-xs">
                            Min: {minDate.toLocaleDateString()} | Max: {maxDate.toLocaleDateString()}
                        </Text>
                    </Space>
                </Card>

                {/* Form Integration */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Form Integration
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Works with Ant Design Form component
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <Form layout="vertical">
                        <Form.Item label="Start Date" name="startDate">
                            <DatePicker placeholder="Select start date" />
                        </Form.Item>
                        <Form.Item label="End Date" name="endDate">
                            <DatePicker showTime placeholder="Select end date and time" />
                        </Form.Item>
                    </Form>
                </Card>

                {/* Sizes */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Sizes
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Small, middle (default), and large variants
                            </Text>
                        </div>
                    }
                    className="mb-6"
                >
                    <Space direction="vertical" className="w-full" size="middle">
                        <div>
                            <Text type="secondary" className="mb-1 block text-xs">
                                Small
                            </Text>
                            <DatePicker size="small" placeholder="Small size" />
                        </div>
                        <div>
                            <Text type="secondary" className="mb-1 block text-xs">
                                Middle (default)
                            </Text>
                            <DatePicker size="middle" placeholder="Middle size" />
                        </div>
                        <div>
                            <Text type="secondary" className="mb-1 block text-xs">
                                Large
                            </Text>
                            <DatePicker size="large" placeholder="Large size" />
                        </div>
                    </Space>
                </Card>

                {/* Implementation Notes */}
                <Card
                    title={
                        <div>
                            <Title level={4} className="!mb-0">
                                Implementation Notes
                            </Title>
                        </div>
                    }
                >
                    <Paragraph className="mb-4 text-muted-foreground italic">
                        Import from{' '}
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                            @client/components/_ui/data-entry/DatePicker
                        </code>
                    </Paragraph>

                    <div className="space-y-4">
                        <div>
                            <Text strong className="mb-2 block">
                                Mobile Detection
                            </Text>
                            <Text type="secondary" className="text-sm">
                                On mobile devices (screen width &lt; 768px), the component renders native HTML5{' '}
                                <code className="rounded bg-muted px-1 text-xs">
                                    &lt;input type=&quot;date&quot;&gt;
                                </code>{' '}
                                or{' '}
                                <code className="rounded bg-muted px-1 text-xs">
                                    &lt;input type=&quot;datetime-local&quot;&gt;
                                </code>{' '}
                                for better UX.
                            </Text>
                        </div>

                        <Divider />

                        <div>
                            <Text strong className="mb-2 block">
                                Output Formats
                            </Text>
                            <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
                                <li>
                                    <strong>Date objects</strong> (default): Standard JavaScript Date objects
                                </li>
                                <li>
                                    <strong>ISO strings</strong> (
                                    <code className="rounded bg-muted px-1 text-xs">outputFormat=&quot;iso&quot;</code>
                                    ): For backwards compatibility with legacy code
                                </li>
                            </ul>
                        </div>

                        <Divider />

                        <div>
                            <Text strong className="mb-2 block">
                                Props
                            </Text>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-separator border-b">
                                            <th className="py-2 pr-4 text-left font-medium">Prop</th>
                                            <th className="px-4 py-2 text-left font-medium">Type</th>
                                            <th className="px-4 py-2 text-left font-medium">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-muted-foreground">
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">value</td>
                                            <td className="px-4 py-2 font-mono text-xs">Date | string | null</td>
                                            <td className="px-4 py-2">Current date value</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">onChange</td>
                                            <td className="px-4 py-2 font-mono text-xs">
                                                (value: Date | string | null) =&gt; void
                                            </td>
                                            <td className="px-4 py-2">Called when date changes</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">outputFormat</td>
                                            <td className="px-4 py-2 font-mono text-xs">
                                                &apos;date&apos; | &apos;iso&apos;
                                            </td>
                                            <td className="px-4 py-2">Output format (default: &apos;date&apos;)</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">dateOnly</td>
                                            <td className="px-4 py-2 font-mono text-xs">boolean</td>
                                            <td className="px-4 py-2">Store at UTC noon (avoids timezone issues)</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">showTime</td>
                                            <td className="px-4 py-2 font-mono text-xs">boolean</td>
                                            <td className="px-4 py-2">Enable time selection</td>
                                        </tr>
                                        <tr className="border-separator border-b">
                                            <td className="py-2 pr-4 font-mono text-xs">minDate</td>
                                            <td className="px-4 py-2 font-mono text-xs">Date | string</td>
                                            <td className="px-4 py-2">Minimum selectable date</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 pr-4 font-mono text-xs">maxDate</td>
                                            <td className="px-4 py-2 font-mono text-xs">Date | string</td>
                                            <td className="px-4 py-2">Maximum selectable date</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </PageController>
    );
}
